import { AnimationType } from '../../types/animated';
import { calculateFadeOpacity, getFadeAnimationStyle, calculateFadeOutUpAnimation, calculateFadeInDownAnimation } from './FadeAnimation';
import { 
  calculateSlideUpAnimation, 
  calculateSlideDownAnimation, 
  calculateSlideLeftAnimation, 
  calculateSlideRightAnimation 
} from './SlideAnimation';
import { calculateScaleUpAnimation, calculateScaleDownAnimation } from './ScaleAnimation';
import { calculateRotateAnimation } from './RotateAnimation';
import { calculateBounceAnimation } from './BounceAnimation';
import { calculateElasticAnimation } from './ElasticAnimation';

/**
 * Animation utility functions for AnimatedItem interface
 * Provides pure functions for calculating animation transforms
 * No external components - integrates with layer item rendering
 */

/**
 * Available animation types in the system
 */
export const SUPPORTED_ANIMATION_TYPES: ReadonlySet<AnimationType> = new Set([
  'none',
  'fade',
  'slide',
  'slide-up',
  'slide-down', 
  'slide-left',
  'slide-right',
  'scale',
  'scale-up',
  'scale-down',
  'rotate',
  'bounce',
  'elastic',
  'fadeOutUp', // Directional fade animation
  'fadeInDown', // Directional fade animation
] as const);

/**
 * Animation calculation functions by type
 * Each function takes (progress, direction) and returns CSS properties
 */
const animationCalculators = {
  none: () => ({}),
  fade: (progress: number, direction: 'entrance' | 'exit') => {
    const opacity = calculateFadeOpacity(progress, direction);
    return getFadeAnimationStyle(opacity);
  },
  slide: calculateSlideUpAnimation, // Default slide is slide-up
  'slide-up': calculateSlideUpAnimation,
  'slide-down': calculateSlideDownAnimation,
  'slide-left': calculateSlideLeftAnimation,
  'slide-right': calculateSlideRightAnimation,
  scale: calculateScaleUpAnimation, // Default scale is scale-up
  'scale-up': calculateScaleUpAnimation,
  'scale-down': calculateScaleDownAnimation,
  rotate: calculateRotateAnimation,
  bounce: calculateBounceAnimation,
  elastic: calculateElasticAnimation,
  fadeOutUp: calculateFadeOutUpAnimation, // Directional fade animation
  fadeInDown: calculateFadeInDownAnimation, // Directional fade animation
} as const;

/**
 * Calculate animation CSS properties for a given animation type
 * @param animationType - Type of animation to calculate
 * @param progress - Animation progress (0 to 1)
 * @param direction - Animation direction ('entrance' or 'exit')
 * @returns CSS properties for the current animation frame
 */
export function calculateAnimationTransform(
  animationType: AnimationType,
  progress: number,
  direction: 'entrance' | 'exit'
): React.CSSProperties {
  if (animationType === 'none') {
    return {};
  }

  const calculator = animationCalculators[animationType as keyof typeof animationCalculators];
  if (!calculator) {
    console.warn(`Animation type "${animationType}" not implemented, falling back to 'none'`);
    return {};
  }

  return calculator(progress, direction);
}

/**
 * Get all available animation types
 * @returns Array of supported animation types
 */
export function getAvailableAnimationTypes(): AnimationType[] {
  return Array.from(SUPPORTED_ANIMATION_TYPES);
}

/**
 * Check if an animation type is supported
 * @param type - Animation type to check
 * @returns True if the animation type is supported
 */
export function isAnimationTypeSupported(type: AnimationType): boolean {
  return SUPPORTED_ANIMATION_TYPES.has(type);
}

/**
 * Export animation utility functions
 */
export { calculateFadeOpacity, getFadeAnimationStyle, calculateFadeOutUpAnimation, calculateFadeInDownAnimation } from './FadeAnimation';
export { 
  calculateSlideTransform, 
  getSlideAnimationStyle,
  calculateSlideUpAnimation,
  calculateSlideDownAnimation,
  calculateSlideLeftAnimation,
  calculateSlideRightAnimation
} from './SlideAnimation';
export { 
  calculateScaleTransform, 
  getScaleAnimationStyle,
  calculateScaleUpAnimation,
  calculateScaleDownAnimation
} from './ScaleAnimation';
export { 
  calculateRotateTransform, 
  getRotateAnimationStyle,
  calculateRotateAnimation
} from './RotateAnimation';
export { 
  calculateBounceTransform, 
  getBounceAnimationStyle,
  calculateBounceAnimation
} from './BounceAnimation';
export { 
  calculateElasticTransform, 
  getElasticAnimationStyle,
  calculateElasticAnimation
} from './ElasticAnimation';
